import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, TrendingUp, TrendingDown, Award, Users, Calendar, Activity } from 'lucide-react';
import { toast } from 'sonner';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const StatsTab = ({ leagueId, token }) => {
  const [teamHistory, setTeamHistory] = useState([]);
  const [professorStats, setProfessorStats] = useState([]);
  const [overview, setOverview] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [totalMatchdays, setTotalMatchdays] = useState(0);

  const fetchStatistics = useCallback(async () => {
    setIsLoading(true);
    try {
      // Fetch all statistics endpoints
      const [historyRes, professorsRes, overviewRes] = await Promise.all([
        fetch(`${API_URL}/api/leagues/${leagueId}/stats/team-points-history`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_URL}/api/leagues/${leagueId}/stats/professor-performance`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_URL}/api/leagues/${leagueId}/stats/overview`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (historyRes.ok) {
        const historyData = await historyRes.json();
        setTeamHistory(historyData.teams || []);
        setTotalMatchdays(historyData.total_matchdays || 0);
      }

      if (professorsRes.ok) {
        const professorsData = await professorsRes.json();
        setProfessorStats(professorsData.professors || []);
      }

      if (overviewRes.ok) {
        const overviewData = await overviewRes.json();
        setOverview(overviewData);
      }
    } catch (err) {
      console.error('Errore caricamento statistiche:', err);
      toast.error('Errore nel caricamento delle statistiche');
    } finally {
      setIsLoading(false);
    }
  }, [leagueId, token]);

  useEffect(() => {
    if (leagueId && token) {
      fetchStatistics();
    }
  }, [leagueId, token, fetchStatistics]);

  // Prepare data for line chart (team points over matchdays)
  const prepareTeamChartData = () => {
    if (!teamHistory || teamHistory.length === 0) return [];
    
    // Get all matchday numbers
    const matchdayNumbers = teamHistory[0]?.history?.map(h => h.matchday) || [];
    
    // Build data points for chart
    return matchdayNumbers.map(matchdayNum => {
      const dataPoint = { matchday: `G${matchdayNum}` };
      
      teamHistory.forEach(team => {
        const matchdayData = team.history.find(h => h.matchday === matchdayNum);
        dataPoint[team.short_name] = matchdayData?.points || 0;
      });
      
      return dataPoint;
    });
  };

  // Prepare data for bar chart (professor comparison)
  const prepareProfessorChartData = () => {
    if (!professorStats || professorStats.length === 0) return [];
    
    // Top 10 professors by total points
    return professorStats.slice(0, 10).map(prof => ({
      name: prof.name,
      punti: prof.total_points,
      eventi: prof.total_events,
      bonus: prof.bonus_count,
      malus: prof.malus_count
    }));
  };

  // Colors for line chart (different color for each team)
  const lineColors = [
    '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6',
    '#ec4899', '#14b8a6', '#f97316', '#06b6d4', '#84cc16'
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const teamChartData = prepareTeamChartData();
  const professorChartData = prepareProfessorChartData();

  return (
    <div className="space-y-6">
      {/* Overview Statistics Cards */}
      {overview && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-white dark:from-blue-950 dark:to-gray-900 border-blue-200 dark:border-blue-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Giornate
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                {overview.calculated_matchdays}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {overview.pending_matchdays} in corso
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-50 to-white dark:from-green-950 dark:to-gray-900 border-green-200 dark:border-green-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400 flex items-center gap-2">
                <Activity className="w-4 h-4" />
                Eventi Totali
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600 dark:text-green-400">
                {overview.total_events}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {overview.total_points_distributed > 0 ? '+' : ''}{overview.total_points_distributed} punti
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-50 to-white dark:from-purple-950 dark:to-gray-900 border-purple-200 dark:border-purple-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400 flex items-center gap-2">
                <Users className="w-4 h-4" />
                Team Attivi
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">
                {overview.total_teams}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {overview.total_members} membri
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-orange-50 to-white dark:from-orange-950 dark:to-gray-900 border-orange-200 dark:border-orange-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400 flex items-center gap-2">
                <Award className="w-4 h-4" />
                Top Team
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-lg font-bold text-orange-600 dark:text-orange-400 truncate">
                {overview.top_team?.team_name || 'N/A'}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {overview.top_team?.points || 0} punti
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Team Points History - Line Chart */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                Andamento Punti Team
              </CardTitle>
              <CardDescription className="mt-1">
                Evoluzione dei punti fantasy per ogni team nel tempo
              </CardDescription>
            </div>
            <Badge variant="outline" className="text-sm">
              {totalMatchdays} Giornate
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {teamChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={teamChartData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                <XAxis 
                  dataKey="matchday" 
                  className="text-sm"
                  stroke="currentColor"
                />
                <YAxis 
                  className="text-sm"
                  stroke="currentColor"
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px'
                  }}
                  labelStyle={{ fontWeight: 'bold', marginBottom: '8px' }}
                />
                <Legend 
                  wrapperStyle={{ paddingTop: '20px' }}
                  iconType="line"
                />
                {teamHistory.map((team, index) => (
                  <Line
                    key={team.team_id}
                    type="monotone"
                    dataKey={team.short_name}
                    stroke={lineColors[index % lineColors.length]}
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                    name={team.short_name}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500 dark:text-gray-400">
              <TrendingUp className="w-12 h-12 mb-3 opacity-50" />
              <p className="text-lg font-medium">Nessun dato disponibile</p>
              <p className="text-sm mt-1">Calcola almeno una giornata per vedere le statistiche</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Professor Performance - Bar Chart */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <TrendingDown className="w-5 h-5 text-green-600" />
                Comparazione Performance Professori
              </CardTitle>
              <CardDescription className="mt-1">
                Top 10 professori per punti totali e numero di eventi
              </CardDescription>
            </div>
            <Badge variant="outline" className="text-sm">
              {professorStats.length} Prof
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {professorChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={professorChartData} layout="horizontal">
                <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                <XAxis 
                  type="number"
                  className="text-sm"
                  stroke="currentColor"
                />
                <YAxis 
                  type="category"
                  dataKey="name" 
                  width={120}
                  className="text-sm"
                  stroke="currentColor"
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px'
                  }}
                  labelStyle={{ fontWeight: 'bold', marginBottom: '8px' }}
                />
                <Legend 
                  wrapperStyle={{ paddingTop: '20px' }}
                />
                <Bar dataKey="punti" fill="#3b82f6" name="Punti Totali" radius={[0, 4, 4, 0]} />
                <Bar dataKey="eventi" fill="#10b981" name="Eventi" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500 dark:text-gray-400">
              <Award className="w-12 h-12 mb-3 opacity-50" />
              <p className="text-lg font-medium">Nessun dato disponibile</p>
              <p className="text-sm mt-1">Aggiungi bonus/malus ai professori per vedere le statistiche</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Professor Details Table */}
      {professorStats.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Dettagli Professori</CardTitle>
            <CardDescription>
              Statistiche complete di tutti i professori della lega
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-3 px-2 font-semibold text-gray-700 dark:text-gray-300">#</th>
                    <th className="text-left py-3 px-2 font-semibold text-gray-700 dark:text-gray-300">Nome</th>
                    <th className="text-left py-3 px-2 font-semibold text-gray-700 dark:text-gray-300">Materia</th>
                    <th className="text-right py-3 px-2 font-semibold text-gray-700 dark:text-gray-300">Punti</th>
                    <th className="text-right py-3 px-2 font-semibold text-gray-700 dark:text-gray-300">Eventi</th>
                    <th className="text-right py-3 px-2 font-semibold text-gray-700 dark:text-gray-300">Bonus</th>
                    <th className="text-right py-3 px-2 font-semibold text-gray-700 dark:text-gray-300">Malus</th>
                    <th className="text-left py-3 px-2 font-semibold text-gray-700 dark:text-gray-300">Team</th>
                  </tr>
                </thead>
                <tbody>
                  {professorStats.map((prof, index) => (
                    <tr 
                      key={prof.professor_id} 
                      className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                    >
                      <td className="py-3 px-2 text-gray-600 dark:text-gray-400">{index + 1}</td>
                      <td className="py-3 px-2 font-medium text-gray-900 dark:text-white">{prof.name}</td>
                      <td className="py-3 px-2 text-gray-600 dark:text-gray-400">{prof.subject}</td>
                      <td className="py-3 px-2 text-right">
                        <span className={`font-semibold ${prof.total_points >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          {prof.total_points > 0 ? '+' : ''}{prof.total_points}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-right text-gray-700 dark:text-gray-300">{prof.total_events}</td>
                      <td className="py-3 px-2 text-right text-green-600 dark:text-green-400">{prof.bonus_count}</td>
                      <td className="py-3 px-2 text-right text-red-600 dark:text-red-400">{prof.malus_count}</td>
                      <td className="py-3 px-2">
                        {prof.assigned_team ? (
                          <Badge variant="outline" className="text-xs">
                            {prof.assigned_team.team_name}
                          </Badge>
                        ) : (
                          <span className="text-gray-400 dark:text-gray-600 text-xs">Non assegnato</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
